/* Atlas V3 — Admin. Native-V3 rebuild of the V1 AdminPanel (cron triggers +
   cron-health + cycle-health). Shell-agnostic: inline styles + studio tokens
   only (no v3web-* classes), so it renders under the desktop shell AND the
   mobile /admin mount in App.jsx. Fetch logic mirrors src/components/admin.jsx;
   the server stays authoritative (ADMIN_USER_ID + CRON_SECRET). */
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { CRONS } from '@/components/adminCrons.js'
import { useIsAdmin } from '@/v2/hooks/useIsAdmin'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const hairline = '1px solid hsl(var(--studio-border) / 0.45)'
const STATUS_COLOR = {
  ok: 'hsl(var(--studio-success))',
  warn: 'hsl(var(--studio-accent))',
  fail: 'hsl(var(--studio-danger))',
}

function Eyebrow({ children, right, first }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, margin: first ? '4px 0 6px' : '36px 0 6px' }}>
      <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))' }}>{children}</div>
      {right}
    </div>
  )
}

// Amber is a line and a label, never a fill — ghost button, accent stroke.
function GhostBtn({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        all: 'unset', cursor: disabled ? 'wait' : 'pointer', boxSizing: 'border-box',
        ...Mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'hsl(var(--studio-accent))', padding: '5px 14px', borderRadius: 4,
        border: '1px solid hsl(var(--studio-accent) / 0.55)',
        opacity: disabled ? 0.45 : 1, whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {children}
    </button>
  )
}

function Msg({ children }) {
  return <div style={{ fontSize: 13, color: 'hsl(var(--studio-text-subtle))', textAlign: 'center', padding: '80px 20px' }}>{children}</div>
}

// Both health endpoints return the same { key,label,status,actual,hint,
// target_display } row shape (see admin.jsx HealthRows).
function HealthRow({ c, first }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', padding: '10px 2px', borderTop: first ? 'none' : hairline, alignItems: 'baseline' }}>
      <span style={{ ...Mono, fontSize: 13, fontWeight: 700, color: STATUS_COLOR[c.status] || 'hsl(var(--studio-text-subtle))', width: 16, flexShrink: 0, textAlign: 'center' }} title={c.status}>
        {c.status === 'ok' ? '✓' : c.status === 'warn' ? '⚠' : '✗'}
      </span>
      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{c.label}</div>
        {c.hint && <div style={{ fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{c.hint}</div>}
      </div>
      {/* value pair wraps below the label as one right-aligned group on narrow screens */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', justifyContent: 'flex-end', marginLeft: 'auto', flexWrap: 'wrap', minWidth: 0 }}>
        <div style={{ textAlign: 'right', minWidth: 0 }}>
          <div style={{ ...Mono, fontSize: 12, fontWeight: 700, color: 'hsl(var(--studio-text))' }}>{c.actual ?? '—'}</div>
          {c.examples?.length > 0 && (
            <div style={{ fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, maxWidth: 220 }}>
              {c.examples.map(e => e.conviction_delta != null ? `${e.ticker} (${e.conviction_delta > 0 ? '+' : ''}${e.conviction_delta})` : e.ticker).join(', ')}
            </div>
          )}
        </div>
        <div style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', textAlign: 'right', maxWidth: 130, minWidth: 0 }}>
          {c.target_display ?? (c.threshold != null ? `≥ ${c.threshold}` : '')}
        </div>
      </div>
    </div>
  )
}

function HealthSection({ title, meta, onRefresh, refreshing, error, checks, first }) {
  return (
    <section>
      <Eyebrow first={first} right={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {meta && <span style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-text-subtle))' }}>{meta}</span>}
          <GhostBtn onClick={onRefresh} disabled={refreshing}>{refreshing ? 'Checking…' : 'Refresh'}</GhostBtn>
        </div>
      }>{title}</Eyebrow>
      {error && <div style={{ fontSize: 11.5, color: 'hsl(var(--studio-danger))', padding: '8px 2px' }}>Health check failed: {error}</div>}
      {!checks && !error && <div style={{ fontSize: 11.5, color: 'hsl(var(--studio-text-subtle))', padding: '8px 2px' }}>Loading…</div>}
      {checks && checks.map((c, i) => <HealthRow key={c.key} c={c} first={i === 0} />)}
    </section>
  )
}

function CronRow({ cron, result, running, onRun, first }) {
  const isOk = result && !result.error && result.status >= 200 && result.status < 300
  const isErr = result && (result.error || result.status >= 400)
  const resColor = isOk ? 'var(--studio-success)' : isErr ? 'var(--studio-danger)' : 'var(--studio-border)'
  return (
    <div style={{ padding: '12px 2px', borderTop: first ? 'none' : hairline }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--studio-text))' }}>{cron.label}</span>
            <code style={{ ...Mono, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.04em' }}>{cron.id}</code>
          </div>
          <div style={{ fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 3, lineHeight: 1.45 }}>{cron.desc}</div>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle) / 0.75)', marginTop: 4 }}>scheduled {cron.schedule}</div>
        </div>
        <GhostBtn onClick={onRun} disabled={running}>{running ? 'Running…' : 'Run'}</GhostBtn>
      </div>
      {result && (
        <pre style={{
          ...Mono, margin: '10px 0 0', padding: '8px 10px', fontSize: 10, lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflow: 'auto',
          borderRadius: 4, border: `1px solid hsl(${resColor} / 0.35)`,
          background: `hsl(${resColor} / 0.06)`,
          color: isOk ? 'hsl(var(--studio-success))' : isErr ? 'hsl(var(--studio-danger))' : 'hsl(var(--studio-text-subtle))',
        }}>
          {result.error
            ? `error: ${result.error} (${result.ms}ms)`
            : `${result.status} (${result.ms}ms)\n${typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)}`.slice(0, 800)
          }
        </pre>
      )}
    </div>
  )
}

export function Admin() {
  const { getToken, isLoaded, isSignedIn } = useAuth()
  const isAdmin = useIsAdmin()
  const [results, setResults] = useState({})
  const [running, setRunning] = useState({})
  const [health, setHealth] = useState(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [healthError, setHealthError] = useState(null)
  const [cycleHealth, setCycleHealth] = useState(null)
  const [cycleHealthError, setCycleHealthError] = useState(null)

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true)
    setHealthError(null)
    try {
      const token = await getToken()
      const res = await fetch('/api/admin/cron-health', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await res.json()
      if (!res.ok) setHealthError(body?.error || `HTTP ${res.status}`)
      else setHealth(body)
    } catch (e) {
      setHealthError(e.message || 'Network error')
    } finally {
      setHealthLoading(false)
    }
  }, [getToken])

  // Cycle data-source freshness — anon endpoint (mirrors cycle-panel), no token.
  const fetchCycleHealth = useCallback(async () => {
    setCycleHealthError(null)
    try {
      const res = await fetch('/api/cycle-health')
      const body = await res.json()
      if (!res.ok || body?.error) setCycleHealthError(body?.error || `HTTP ${res.status}`)
      else setCycleHealth(body)
    } catch (e) {
      setCycleHealthError(e.message || 'Network error')
    }
  }, [])

  useEffect(() => {
    if (isLoaded && isSignedIn && isAdmin) { fetchHealth(); fetchCycleHealth() }
  }, [isLoaded, isSignedIn, isAdmin, fetchHealth, fetchCycleHealth])

  if (!isLoaded) return <Msg>Loading…</Msg>
  if (!isSignedIn) return <Msg>Sign in required.</Msg>
  if (!isAdmin) return <Msg>Admin access only.</Msg>

  async function runCron(id) {
    setRunning(s => ({ ...s, [id]: true }))
    setResults(s => ({ ...s, [id]: null }))
    const t0 = Date.now()
    try {
      const token = await getToken()
      const res = await fetch('/api/admin/run-cron', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cron: id }),
      })
      const text = await res.text()
      let parsed
      try { parsed = JSON.parse(text) } catch { parsed = text }
      setResults(s => ({ ...s, [id]: { status: res.status, body: parsed, ms: Date.now() - t0 } }))
    } catch (e) {
      setResults(s => ({ ...s, [id]: { error: e.message, ms: Date.now() - t0 } }))
    } finally {
      setRunning(s => ({ ...s, [id]: false }))
    }
  }

  const cycleMeta = cycleHealth
    ? `${cycleHealth.ok ? 'all fresh' : `${cycleHealth.summary.fail} down · ${cycleHealth.summary.warn} degraded`} · ${cycleHealth.summary.total} sources`
    : null
  const healthMeta = health
    ? `checked ${new Date(health.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : null

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))' }}>Operations</div>
        <div style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 40, lineHeight: 1.02, color: 'hsl(var(--studio-text))', marginTop: 6 }}>Admin</div>
        <div style={{ fontSize: 11.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 8 }}>
          Manually fire any cron. Server enforces admin via ADMIN_USER_ID + CRON_SECRET.
        </div>
      </div>

      <HealthSection
        first
        title="Cron health"
        meta={healthMeta}
        onRefresh={fetchHealth}
        refreshing={healthLoading}
        error={healthError}
        checks={health?.checks}
      />

      <HealthSection
        title="Cycle freshness"
        meta={cycleMeta}
        onRefresh={fetchCycleHealth}
        refreshing={false}
        error={cycleHealthError}
        checks={cycleHealth?.checks}
      />

      <section>
        <Eyebrow>Cron triggers</Eyebrow>
        {CRONS.map((c, i) => (
          <CronRow
            key={c.id}
            cron={c}
            result={results[c.id]}
            running={running[c.id]}
            onRun={() => runCron(c.id)}
            first={i === 0}
          />
        ))}
      </section>
    </div>
  )
}
